import { edgeAxis, edgeIsMax } from '@sim/core/base';
import type { Direction, GridPos, GridRect } from '@sim/core/types';

/** Below this, a press-and-release resolves as a plain click rather than a drag. */
export const DRAG_THRESHOLD_PX = 8;

/**
 * Distinguishes a click held in place — which always drifts a pixel or two — from a deliberate
 * drag, so an ambiguous press only commits to the drag once it has travelled far enough.
 */
export function exceedsDragThreshold(
  from: { x: number; y: number },
  to: { x: number; y: number },
  thresholdPx: number,
): boolean {
  return Math.hypot(to.x - from.x, to.y - from.y) >= thresholdPx;
}

export function directionBetween(from: GridPos, to: GridPos): Direction | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === 0 && dy === 0) return null;
  return Math.abs(dx) >= Math.abs(dy) ? (dx > 0 ? 'east' : 'west') : dy > 0 ? 'south' : 'north';
}

export function cellAtLocalPoint(
  local: { x: number; y: number },
  gridSize: { width: number; height: number },
  cellSize: number,
): GridPos | null {
  const pos = { x: Math.floor(local.x / cellSize), y: Math.floor(local.y / cellSize) };
  const inside = pos.x >= 0 && pos.y >= 0 && pos.x < gridSize.width && pos.y < gridSize.height;
  return inside ? pos : null;
}

/**
 * Hit-tests a raw pixel point against the Subscription's four wall lines (not cells), with a
 * small tolerance for grabbing.
 */
export function hitWallAt(
  point: { x: number; y: number },
  rect: GridRect | null,
  cellSize: number,
  tolerance: number,
): Direction | null {
  if (!rect) return null;
  const eastX = (rect.max.x + 1) * cellSize;
  const northY = rect.min.y * cellSize;
  const southY = (rect.max.y + 1) * cellSize;
  const westX = rect.min.x * cellSize;
  const withinVerticalSpan = point.y >= northY - tolerance && point.y <= southY + tolerance;
  const withinHorizontalSpan = point.x >= westX - tolerance && point.x <= eastX + tolerance;
  if (withinVerticalSpan && Math.abs(point.x - eastX) <= tolerance) return 'east';
  if (withinVerticalSpan && Math.abs(point.x - westX) <= tolerance) return 'west';
  if (withinHorizontalSpan && Math.abs(point.y - northY) <= tolerance) return 'north';
  if (withinHorizontalSpan && Math.abs(point.y - southY) <= tolerance) return 'south';
  return null;
}

export function edgeValue(rect: GridRect, edge: Direction): number {
  const axis = edgeAxis(edge);
  return edgeIsMax(edge) ? rect.max[axis] : rect.min[axis];
}

export function targetCellFromPoint(
  point: { x: number; y: number },
  axis: 'x' | 'y',
  cellSize: number,
): number {
  return Math.floor((axis === 'x' ? point.x : point.y) / cellSize);
}
